import React, { useState, useEffect } from 'react';
import { paymentRemindersService } from '../../services/paymentRemindersService';
import { itinerariesService } from '../../services/itinerariesService';
import './PaymentRemindersList.css';

const PaymentRemindersList = () => {
  const [reminders, setReminders] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [formData, setFormData] = useState({
    itinerary_id: '',
    payment_type: 'deposit',
    payment_amount: '',
    reminder_date: '',
    due_date: '',
    custom_message: '',
    notify_agent: true,
    notify_agent_sms: false,
    notify_client: true,
    notify_client_sms: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [remindersResponse, itinerariesResponse] = await Promise.all([
        paymentRemindersService.getAll(),
        itinerariesService.getAll()
      ]);
      
      // Handle response structure
      const remindersData = remindersResponse.data || remindersResponse;
      const itinerariesData = itinerariesResponse.data || itinerariesResponse;
      
      setReminders(Array.isArray(remindersData) ? remindersData : []);
      setItineraries(Array.isArray(itinerariesData) ? itinerariesData : []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load payment reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReminder = async (e) => {
    e.preventDefault();
    try {
      await paymentRemindersService.create(formData);
      setShowCreateModal(false);
      resetForm();
      await fetchData();
    } catch (error) {
      console.error('Error creating reminder:', error);
      alert('Failed to create payment reminder');
    }
  };

  const handleUpdateReminder = async (e) => {
    e.preventDefault();
    try {
      await paymentRemindersService.update(selectedReminder.id, formData);
      setShowEditModal(false);
      setSelectedReminder(null);
      resetForm();
      await fetchData();
    } catch (error) {
      console.error('Error updating reminder:', error);
      alert('Failed to update payment reminder');
    }
  };

  const handleDeleteReminder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment reminder?')) {
      return;
    }
    
    try {
      await paymentRemindersService.delete(id);
      await fetchData();
    } catch (error) {
      console.error('Error deleting reminder:', error);
      alert('Failed to delete payment reminder');
    }
  };

  const handleSendNow = async (id) => {
    if (!window.confirm('Are you sure you want to send this reminder now?')) {
      return;
    }
    
    try {
      await paymentRemindersService.sendNow(id);
      await fetchData();
      alert('Payment reminder sent successfully!');
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Failed to send payment reminder');
    }
  };

  const handleEditClick = (reminder) => {
    setSelectedReminder(reminder);
    setFormData({
      itinerary_id: reminder.itinerary_id,
      payment_type: reminder.payment_type,
      payment_amount: reminder.payment_amount,
      reminder_date: reminder.reminder_date.split('T')[0],
      due_date: reminder.due_date.split('T')[0],
      custom_message: reminder.custom_message || '',
      notify_agent: reminder.notify_agent,
      notify_agent_sms: reminder.notify_agent_sms,
      notify_client: reminder.notify_client,
      notify_client_sms: reminder.notify_client_sms
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      itinerary_id: '',
      payment_type: 'deposit',
      payment_amount: '',
      reminder_date: '',
      due_date: '',
      custom_message: '',
      notify_agent: true,
      notify_agent_sms: false,
      notify_client: true,
      notify_client_sms: false
    });
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      scheduled: 'badge-scheduled',
      sent: 'badge-sent',
      failed: 'badge-failed'
    };
    return statusClasses[status] || 'badge-default';
  };

  if (loading) {
    return <div className="loading">Loading payment reminders...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="payment-reminders-list">
      <div className="reminders-header">
        <h1>Payment Reminders</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}
        >
          Create Reminder
        </button>
      </div>

      <div className="reminders-stats">
        <div className="stat-card">
          <div className="stat-value">{reminders.filter(r => r.status === 'scheduled').length}</div>
          <div className="stat-label">Scheduled</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{reminders.filter(r => r.status === 'sent').length}</div>
          <div className="stat-label">Sent</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{reminders.filter(r => new Date(r.due_date) < new Date()).length}</div>
          <div className="stat-label">Overdue</div>
        </div>
      </div>

      {reminders.length === 0 ? (
        <div className="no-reminders">
          <p>No payment reminders scheduled.</p>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            Create Your First Reminder
          </button>
        </div>
      ) : (
        <div className="reminders-table-container">
          <table className="reminders-table">
            <thead>
              <tr>
                <th>Itinerary</th>
                <th>Client</th>
                <th>Payment Type</th>
                <th>Amount</th>
                <th>Reminder Date</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reminders.map(reminder => (
                <tr key={reminder.id}>
                  <td>
                    <div className="itinerary-info">
                      <strong>{reminder.itinerary_title}</strong>
                    </div>
                  </td>
                  <td>{reminder.client_name}</td>
                  <td className="capitalize">{reminder.payment_type.replace('_', ' ')}</td>
                  <td>${parseFloat(reminder.payment_amount).toFixed(2)}</td>
                  <td>{new Date(reminder.reminder_date).toLocaleDateString()}</td>
                  <td>
                    <div className={new Date(reminder.due_date) < new Date() ? 'overdue' : ''}>
                      {new Date(reminder.due_date).toLocaleDateString()}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(reminder.status)}`}>
                      {reminder.status}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      {reminder.status === 'scheduled' && (
                        <>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleSendNow(reminder.id)}
                          >
                            Send Now
                          </button>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEditClick(reminder)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteReminder(reminder.id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {reminder.status === 'sent' && (
                        <span className="sent-info">
                          Sent on {new Date(reminder.sent_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Payment Reminder</h2>
              <button className="close-button" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateReminder}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Itinerary *</label>
                  <select
                    value={formData.itinerary_id}
                    onChange={(e) => setFormData({...formData, itinerary_id: e.target.value})}
                    className="form-input"
                    required
                  >
                    <option value="">Select an itinerary</option>
                    {itineraries.map(itinerary => (
                      <option key={itinerary.id} value={itinerary.id}>
                        {itinerary.title} - {itinerary.first_name} {itinerary.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Payment Type *</label>
                  <select
                    value={formData.payment_type}
                    onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
                    className="form-input"
                    required
                  >
                    <option value="deposit">Deposit</option>
                    <option value="final_payment">Final Payment</option>
                    <option value="installment">Installment</option>
                    <option value="balance_due">Balance Due</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Payment Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.payment_amount}
                    onChange={(e) => setFormData({...formData, payment_amount: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Reminder Date *</label>
                    <input
                      type="date"
                      value={formData.reminder_date}
                      onChange={(e) => setFormData({...formData, reminder_date: e.target.value})}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Payment Due Date *</label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Custom Message (Optional)</label>
                  <textarea
                    value={formData.custom_message}
                    onChange={(e) => setFormData({...formData, custom_message: e.target.value})}
                    className="form-input"
                    rows="3"
                    placeholder="Add a personalized message to the reminder..."
                  />
                </div>

                <div className="notification-options">
                  <h3>Notification Settings</h3>
                  
                  <div className="notification-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.notify_agent}
                        onChange={(e) => setFormData({...formData, notify_agent: e.target.checked})}
                      />
                      Notify Agent by Email
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.notify_agent_sms}
                        onChange={(e) => setFormData({...formData, notify_agent_sms: e.target.checked})}
                      />
                      Notify Agent by SMS
                    </label>
                  </div>

                  <div className="notification-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.notify_client}
                        onChange={(e) => setFormData({...formData, notify_client: e.target.checked})}
                      />
                      Notify Client by Email
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.notify_client_sms}
                        onChange={(e) => setFormData({...formData, notify_client_sms: e.target.checked})}
                      />
                      Notify Client by SMS
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Reminder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Payment Reminder</h2>
              <button className="close-button" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateReminder}>
              <div className="modal-body">
                {/* Same form fields as create modal */}
                <div className="form-group">
                  <label>Payment Type *</label>
                  <select
                    value={formData.payment_type}
                    onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
                    className="form-input"
                    required
                  >
                    <option value="deposit">Deposit</option>
                    <option value="final_payment">Final Payment</option>
                    <option value="installment">Installment</option>
                    <option value="balance_due">Balance Due</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Payment Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.payment_amount}
                    onChange={(e) => setFormData({...formData, payment_amount: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Reminder Date *</label>
                    <input
                      type="date"
                      value={formData.reminder_date}
                      onChange={(e) => setFormData({...formData, reminder_date: e.target.value})}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Payment Due Date *</label>
                    <input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                      className="form-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Custom Message (Optional)</label>
                  <textarea
                    value={formData.custom_message}
                    onChange={(e) => setFormData({...formData, custom_message: e.target.value})}
                    className="form-input"
                    rows="3"
                  />
                </div>

                <div className="notification-options">
                  <h3>Notification Settings</h3>
                  
                  <div className="notification-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.notify_agent}
                        onChange={(e) => setFormData({...formData, notify_agent: e.target.checked})}
                      />
                      Notify Agent by Email
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.notify_agent_sms}
                        onChange={(e) => setFormData({...formData, notify_agent_sms: e.target.checked})}
                      />
                      Notify Agent by SMS
                    </label>
                  </div>

                  <div className="notification-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.notify_client}
                        onChange={(e) => setFormData({...formData, notify_client: e.target.checked})}
                      />
                      Notify Client by Email
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.notify_client_sms}
                        onChange={(e) => setFormData({...formData, notify_client_sms: e.target.checked})}
                      />
                      Notify Client by SMS
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Reminder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentRemindersList;
